import { useCallback, useMemo, useState } from 'react';
import { IconButton, Stack, Typography, Button, Modal } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';

import { Table, ITableCell, ITableRow } from '@/components/shared/Table';
import { FileUpload } from '@/components/shared/FileUploads/FileUpload';
import { Input } from '@/components/shared/Inputs/Input';

import { useUIContext, useLoading } from '@/shared/context/UIContextProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';

import { uploadUnitAttachment } from '@/data';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Unit } from '@urbanmix-tech/shared-js';

export type FilesTabContentProps = {
  unit: Unit;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateUnit(updateData: Record<string, any>): void;
};

export function FilesTabContent({ unit, onUpdateUnit }: FilesTabContentProps) {
  const { openConfirm } = useUIContext();
  const { showAlert } = useAlert();
  const { createLoading } = useLoading();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(
    null
  );

  const files = useMemo(() => unit?.files || [], [unit?.files]);

  const handleOpenUploadModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveFile = async () => {
    if (!file || !unit) {
      showAlert('You are not ready to upload a file', 'error');
      return;
    }

    const trimmedName = filename.trim();
    if (!trimmedName) {
      showAlert('Please input a filename', 'error');
      return;
    }

    if (files.find((item) => item.name === trimmedName)) {
      showAlert('Duplicate filename. Please choose another', 'error');
      return;
    }

    const loader = createLoading();
    loader.startLoading({ title: 'Uploading...' });

    try {
      const url = await uploadUnitAttachment(
        file,
        trimmedName,
        unit.projectId,
        unit.id
      );

      if (!url) {
        showAlert('Failed at uploading a file', 'error');
        return;
      }

      await onUpdateUnit({
        files: [...files, { name: trimmedName, url }],
      });

      handleCloseModal();
    } catch (err) {
      console.error('File upload error:', err);
      showAlert(
        err instanceof Error ? err.message : 'Failed at uploading a file',
        'error'
      );
    } finally {
      loader.stopLoading();
    }
  };

  const handleDeleteFile = useCallback(
    (name: string) => {
      openConfirm({
        title: 'Remove file',
        description: 'Are you sure?',
        onAction: (value: boolean) => {
          if (value) {
            const newFiles = files.filter((item) => item.name !== name);

            onUpdateUnit({ files: newFiles });
          }
        },
      });
    },
    [files, onUpdateUnit, openConfirm]
  );

  const handleDownloadFile = useCallback(
    async (url: string, name: string) => {
      try {
        setDownloadingFileName(name);
        const functions = getFunctions();

        const getSignedDownloadURL = httpsCallable(
          functions,
          'getSignedDownloadURL'
        );
        const result = (await getSignedDownloadURL({
          url,
        })) as { data: { presignedUrl: string; expires: number } };

        if (result.data && result.data.presignedUrl) {
          window.location.href = result.data.presignedUrl;
        } else {
          showAlert('Failed to get download URL', 'error');
        }
      } catch (err) {
        console.error('Error downloading file:', err);
        showAlert('Failed to download file', 'error');
      } finally {
        setDownloadingFileName(null);
      }
    },
    [showAlert]
  );

  const handleChangeFile = (changedFile: File) => {
    setFile(changedFile);
    setFilename(changedFile.name);
  };

  const columns: ITableCell[] = [
    {
      label: 'Name',
      sortable: true,
    },
    {
      label: '',
      sortable: false,
    },
  ];

  const rows: ITableRow[] = useMemo(() => {
    if (!files) {
      return [];
    }

    return files.map((item) => {
      const data: Record<string, ITableCell> = {
        Name: {
          label: item.name,
          value: item.name,
          children: <div className="w-full overflow-x-auto">{item.name}</div>,
        },
        URL: {
          label: item.url,
          children: <div className="w-[400px] overflow-x-auto">{item.url}</div>,
        },
        '': {
          label: '',
          children: (
            <>
              <IconButton
                onClick={() => handleDownloadFile(item.url, item.name)}
                className="p-0"
              >
                {downloadingFileName === item.name ? (
                  <CircularProgress size={20} />
                ) : (
                  <FileDownloadIcon className="font-[18px] text-stone-200" />
                )}
              </IconButton>
              <IconButton
                onClick={() => handleDeleteFile(item.name)}
                className="p-0"
              >
                <DeleteIcon className="font-[18px] text-stone-200" />
              </IconButton>
            </>
          ),
        },
      };

      return {
        id: item.name,
        data,
      } as ITableRow;
    });
  }, [files, handleDeleteFile, handleDownloadFile, downloadingFileName]);

  const helperTexts = useMemo(() => {
    const texts: string[] = [];
    if (file) {
      if (filename.trim() === '') {
        texts.push('Please input filename');
      }
      if (files.find((item) => item.name === filename.trim())) {
        texts.push('Duplicate filename. Please choose another');
      }
    }
    return texts;
  }, [file, filename, files]);

  const isSaveDisabled = !file || helperTexts.length > 0;

  return (
    <div className="h-full w-[640px]">
      <div className="flex w-full items-center justify-end p-15">
        <Button
          variant="contained"
          className="w-fit px-16 py-8"
          onClick={handleOpenUploadModal}
        >
          <FileUploadIcon />
          Upload file
        </Button>
      </div>
      <Stack className="h-[calc(100%-36px)] bg-white/5 p-16" gap="16px">
        <Typography variant="caption" className="tracking-tight text-stone-200">
          {files?.length || 0} files
        </Typography>
        <Table
          rows={rows}
          columns={columns}
          stickyHeader
          height="calc(100% - 32px)"
          px={8}
        />
      </Stack>
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        className="flex items-center justify-center"
      >
        <div className="w-1/3 rounded-[10px] border border-stone-200 border-opacity-[0.15] bg-gray-950 p-16 shadow outline-none">
          <div className="mb-15">
            <FileUpload onChangeFile={handleChangeFile} />
          </div>
          <div className="mb-15">
            <Input
              onChange={(e) => setFilename(e.target.value)}
              value={filename}
              type="text"
              fullWidth
              placeholder="Change file name*"
              className="border border-primary !bg-white !text-black placeholder-black"
              required
            />
            {helperTexts.map((helper) => (
              <Typography
                key={helper}
                variant="caption3"
                className="text-red-400"
              >
                {helper}
              </Typography>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveFile}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
